import { createSelector } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import type { RootState } from 'src/root/store';

import type {
  CounselorMeritBadge,
  UpdateBulkMeritBadgeRequirements,
} from '@appTypes/esb';
import { formatToAPIDate } from '@utils/datesLuxon';

const moduleSelector = (state: RootState) => state.mbcBulkEntry;

export const mbSelectionSel = createSelector(
  [
    state => moduleSelector(state).mb,
    state => moduleSelector(state).counselorMB,
  ],
  (mb, counselorMB) => ({
    mb,
    counselorMB,
  }),
);

export const hasSelectionSel = createSelector(
  mbSelectionSel,
  state => state.counselorMB && state.mb,
);

export const selectedVersionSel = createSelector(
  moduleSelector,
  state => state.selectedVersion,
);

export const selectedYouthSel = createSelector(
  moduleSelector,
  state => state.selectedYouth,
);

export const selectedYouthByMIDSel = createSelector(
  selectedYouthSel,
  youth => new Map(youth.map(item => [item.memberId, item])),
);

export const selectedYouthByUserIdSel = createSelector(
  selectedYouthSel,
  youth =>
    new Map(
      youth
        .filter(item => item.userId != null)
        .map(item => [item.userId!, item]),
    ),
);

const mbcProfileSel = createSelector(moduleSelector, state => state.mbcProfile);

/** MBs the counselor can currently enter requirements for */
export const isActiveCounselorMB = (mb: CounselorMeritBadge) => {
  const effectiveDt = DateTime.fromISO(mb.effectiveDt!);
  const expiryDt = DateTime.fromISO(mb.expiryDt!);
  const today = DateTime.now();
  if (effectiveDt > today) return false;
  if (expiryDt < today) return false;

  return true;
};

export const filteredMBSel = createSelector(mbcProfileSel, mbcProfile =>
  (mbcProfile?.meritBadges || []).filter(isActiveCounselorMB),
);

export const validationErrorsSel = createSelector(
  moduleSelector,
  state => state.validationErrors,
);

export const selectedRequirementsSel = createSelector(
  moduleSelector,
  state => state.selectedRequirements,
);

export const selectedRequirementsSetSel = createSelector(
  selectedRequirementsSel,
  reqs => new Set(reqs),
);

export const completionDateSel = createSelector(
  moduleSelector,
  state => state.completionDate,
);

export const commentSel = createSelector(
  moduleSelector,
  state => state.comment,
);

export const counselorApprovedSel = createSelector(
  moduleSelector,
  state => state.counselorApproved,
);

export const canSubmitSel = createSelector(
  [selectedYouthSel, selectedRequirementsSel, completionDateSel, commentSel],
  (youth, reqs, date, comment) =>
    youth.length > 0 && reqs.length > 0 && Boolean(date || comment),
);

export const submitPayloadSel = createSelector(
  [
    selectedYouthSel,
    selectedRequirementsSel,
    selectedVersionSel,
    completionDateSel,
    counselorApprovedSel,
    commentSel,
  ],
  (youth, reqs, version, completionDate, counselorApproved, comment) => {
    if (!version || youth.length === 0 || reqs.length === 0) return [];

    const counselorApprovedDate = counselorApproved
      ? formatToAPIDate(new Date(Date.now()))
      : undefined;

    const payload: UpdateBulkMeritBadgeRequirements[] = youth
      .filter(y => y.userId != null)
      .map(y => ({
        userId: y.userId!,
        versionId: Number(version.versionId),
        requirements: reqs.map(reqId => ({
          markedCompletedDate: completionDate
            ? formatToAPIDate(new Date(Date.now()))
            : undefined,
          id: reqId,
          started: completionDate ? true : undefined,
          completed: completionDate ? true : undefined,
          dateCompleted: completionDate
            ? formatToAPIDate(completionDate)
            : undefined,
          counselorApprovedDate,
          comments: comment ? { body: comment } : undefined,
        })),
      }));

    return payload;
  },
);
